<template>
  <div>
    <!-- Existing component structure -->
    <div class="items-center flex-col sm:flex xl:block 2xl:flex sm:space-x-4 xl:space-x-0 2xl:space-x-4">
      <!-- Conditional rendering based on whether an image is available -->
      <div v-if="previewImageUrl" class="profile-picture-preview-container mx-auto w-max">
        <img :src="previewImageUrl" alt="Profile picture" class="profile-picture-preview mb-4 rounded-lg w-28 h-28 sm:mb-0 xl:mb-4 2xl:mb-0" />
      </div>
      <div v-else class="flex items-center justify-center w-full h-36 bg-gray-300 rounded w-full dark:bg-gray-700 mb-3">
        <svg class="w-10 h-10 text-gray-200 dark:text-gray-600" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 18">
          <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z"></path>
        </svg>
      </div>

      <div class="w-full">
        <h3 class="mb-1 text-xl font-bold text-gray-900 dark:text-white">
          Profile picture
        </h3>
        <div class="mb-4 text-sm text-gray-500 dark:text-gray-400">
          JPG, GIF or PNG. Max size of 800K
        </div>

        <div class="flex items-center justify-center w-full max-w-[231px] mb-3">
          <label for="dropzone-file" class="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600">
            <div class="flex flex-col items-center justify-center pt-3 pb-2">
              <svg class="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
              </svg>
              <p class="mb-2 text-sm text-gray-500 dark:text-gray-400 p-1"><span class="font-semibold">Click to upload</span> or drag and drop</p>
              <p class="text-xs text-gray-500 dark:text-gray-400 text-center p-1">SVG, PNG, JPG or GIF (MAX. 800x400px)</p>
            </div>
            <input id="dropzone-file" hidden type="file" @change="handleFileChange" />
          </label>
        </div>

        <div class="flex items-center space-x-4">
          <button @click="uploadImage" type="button" class="inline-flex items-center px-3 py-2 text-sm font-medium text-center text-white rounded-lg bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800">
            <svg class="w-4 h-4 mr-2 -ml-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z"></path>
              <path d="M9 13h2v5a1 1 0 11-2 0v-5z"></path>
            </svg>
            Upload picture
          </button>

          <button @click="deleteImage" type="button" class="py-2 px-3 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-200 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700">
            Delete
          </button>
        </div>
      </div>
    </div>

    <!-- Status Informer -->
    <transition name="slide-fade">
      <div v-if="statusVisible" class="status-informer fixed right-2 bottom-2 group flex flex-col items-center justify-center rounded-xl bg-teal-200 p-2.5 hover:bg-teal-100 dark:bg-teal-900 dark:hover:bg-teal-800 cursor-pointer">
        {{ statusMessage }}
      </div>
    </transition>
  </div>
</template>


<script>
export default {
  data() {
    return {
      selectedFile: null,
      previewImageUrl: null, // Start with null to trigger the placeholder initially
      statusVisible: false,  // Controls the visibility of the status informer
      statusMessage: '',     // Holds the status message to be displayed
    };
  },
  async mounted() {
    await this.loadUserImage();
  },
  methods: {
    async loadUserImage() {
      try {
        const userId = document.getElementById('edit-page__user-id').value; // Get the user ID from the hidden input
        const response = await fetch(`/wp-json/custom/v1/get-user-photo/?user_id=${userId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.image_url) {
            this.previewImageUrl = data.image_url;
          } else {
            this.previewImageUrl = null; // No image, use placeholder
          }
        } else {
          console.error('Failed to load user data:', response.statusText);
          this.previewImageUrl = null; // Fallback to placeholder
        }
      } catch (error) {
        console.error('Error:', error);
        this.previewImageUrl = null; // Fallback to placeholder
      }
    },
    handleFileChange(event) {
      this.selectedFile = event.target.files[0];
      if (this.selectedFile) {
        this.previewImageUrl = URL.createObjectURL(this.selectedFile);
      }
    },
    async uploadImage() {
      if (!this.selectedFile) {
        this.showStatusMessage('Please select a file first.');
        return;
      }

      const formData = new FormData();
      formData.append('file', this.selectedFile);

      try {
        const response = await fetch('/wp-json/wp/v2/media', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`, // Assuming you use JWT or similar auth
            'Content-Disposition': `attachment; filename="${this.selectedFile.name}"`,
          },
          body: formData,
        });

        if (response.ok) {
          const mediaData = await response.json();
          await this.updateUserPhotoACF(mediaData.id);
          this.previewImageUrl = mediaData.source_url;
          this.showStatusMessage('Your profile picture was updated successfully.');
        } else {
          console.error('Upload failed:', response.statusText);
          this.showStatusMessage('Failed to upload profile picture.');
        }
      } catch (error) {
        console.error('Error:', error);
        this.showStatusMessage('An error occurred while uploading your profile picture.');
      }
    },
    async updateUserPhotoACF(mediaId) {
      try {
        const userId = this.getUserId(); // Replace this with your method to get the current user ID
        const response = await fetch('/wp-json/custom/v1/update-user-photo/', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userId,
            media_id: mediaId,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          console.log('User photo updated successfully:', data.message);
        } else {
          console.error('Update failed:', response.statusText);
          this.showStatusMessage('Failed to update profile picture.');
        }
      } catch (error) {
        console.error('Error:', error);
        this.showStatusMessage('An error occurred while updating your profile picture.');
      }
    },
    async deleteImage() {
      try {
        const userId = this.getUserId(); // Replace this with your method to get the current user ID
        const response = await fetch('/wp-json/custom/v1/update-user-photo/', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userId,
            media_id: '', // Pass an empty string or null to remove the image
          }),
        });

        if (response.ok) {
          this.previewImageUrl = null; // Reset to null to trigger the placeholder
          this.showStatusMessage('Your profile picture was deleted successfully.');
        } else {
          console.error('Delete failed:', response.statusText);
          this.showStatusMessage('Failed to delete profile picture.');
        }
      } catch (error) {
        console.error('Error:', error);
        this.showStatusMessage('An error occurred while deleting your profile picture.');
      }
    },
    getUserId() {
      let userIdInput = document.querySelector('#edit-page__user-id').value;
      return userIdInput;
    },
    showStatusMessage(message) {
      this.statusMessage = message;
      this.statusVisible = true;
      setTimeout(() => {
        this.statusVisible = false;
      }, 3000); // Hide after 3 seconds
    }
  },
};
</script>


<style scoped>
.status-informer {
  opacity: 0;
  transform: translateX(100%);
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.slide-fade-enter,
.slide-fade-leave-to

/* .slide-fade-leave-active in <2.1.8 */
  {
  opacity: 0;
  transform: translateX(100%);
}

.status-informer.show {
  opacity: 1;
  transform: translateX(0);
}
</style>
